import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { CalendarModule } from 'primeng/calendar';
import { CheckboxModule } from 'primeng/checkbox';
import { DividerModule } from 'primeng/divider';
import { DropdownModule } from 'primeng/dropdown';
import {
  DialogService,
  DynamicDialogComponent,
  DynamicDialogRef,
} from 'primeng/dynamicdialog';
import { InputTextModule } from 'primeng/inputtext';
import { MenubarModule } from 'primeng/menubar';
import { CourseType } from 'src/app/models/course-type.model';
import { LERNFELD_PERMISSIONS } from 'src/app/components/menu/lernfelder/lernfeld-permissions';
import { Course } from 'src/app/models/course.model';
import { Occupation } from 'src/app/models/lernfeld-content.model';
import { Location } from 'src/app/models/location.model';
import { user } from 'src/app/models/user.model';
import { AuthService } from 'src/app/services/auth.service';
import { CourseService } from 'src/app/services/course.service';
import { DialogManagerService } from 'src/app/services/dialog-manager.service';
import { LernfeldContentService } from 'src/app/services/lernfeld-content.service';
import { NotificationService } from 'src/app/services/notification.service';

@Component({
  selector: 'app-course-list-course-detail-dialog',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    CalendarModule,
    DropdownModule,
    CheckboxModule,
    InputTextModule,
    MenubarModule,
    DividerModule,
  ],
  templateUrl: './course-list-course-detail-dialog.html',
  styleUrl: './course-list-course-detail-dialog.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CourseListCourseDetailDialog {
  instance?: DynamicDialogComponent;

  isEditing = false;
  editedCourse: Course = null;

  // Übergebene Daten aus dem Dialog-Aufruf
  course?: Course;
  courseTypes: CourseType[] = [];
  locations: Location[] = [];
  allUsers: user[] = [];
  occupations: Occupation[] = [];
  timetableNameOptions: string[] = [];

  constructor(
    private readonly cdr: ChangeDetectorRef,
    private readonly lernfeldContentService: LernfeldContentService,
    private readonly ref: DynamicDialogRef,
    private readonly authService: AuthService,
    private readonly dialogService: DialogService,
    private readonly courseService: CourseService,
    private readonly notificationService: NotificationService,
    private readonly dialogManagerService: DialogManagerService,
  ) {
    this.instance = this.dialogService.getInstance(this.ref);
    if (this.instance && this.instance.data) {
      this.course = this.instance.data['course'];
      this.courseTypes = this.instance.data['courseTypes'];
      this.locations = this.instance.data['locations'];
      this.allUsers = this.instance.data['allUsers'];
    }
  }

  get canWriteCourse(): boolean {
    return this.authService.getUserPermissions().includes('Course.Write');
  }

  // Liste der Berufe liegt hinter Lernfeld.Read; ohne das Recht nur anzeigen
  get canReadOccupations(): boolean {
    return this.authService
      .getUserPermissions()
      .includes(LERNFELD_PERMISSIONS.read);
  }

  closeDialog(): void {
    this.isEditing = false;
    this.editedCourse = null;
    this.ref.close();
  }

  //#region Edit Mode

  startEditing() {
    this.isEditing = true;
    this.loadOccupations();
    this.loadTimetableNameOptions();

    this.editedCourse = {
      ...this.course,
      courseType: this.getCourseType(this.course.courseTypeId),
      location: this.getLocation(this.course.locationId),
      startDateObj: this.course.startDate
        ? new Date(this.course.startDate)
        : null,
      endDateObj: this.course.endDate ? new Date(this.course.endDate) : null,
      apt1DateObj: this.course.apt1Date ? new Date(this.course.apt1Date) : null,
      apt2DateObj: this.course.apt2Date ? new Date(this.course.apt2Date) : null,
    };
  }

  private loadOccupations(): void {
    if (!this.canReadOccupations || this.occupations.length > 0) return;

    this.lernfeldContentService.getOccupations().subscribe({
      next: (data) => {
        this.occupations = [...data].sort((a, b) => a.name.localeCompare(b.name));
        this.cdr.markForCheck();
      },
      error: () => console.error('Ausbildungsberufe nicht geladen!'),
    });
  }

  private loadTimetableNameOptions(): void {
    this.courseService.getTimetableNameOptions().subscribe({
      next: (data) => {
        // Gespeicherter Name ohne Stundenplan-Eintrag: sonst leer angezeigt und beim Speichern verloren
        const current = this.course.timetableName;
        this.timetableNameOptions =
          current && !data.includes(current) ? [current, ...data] : data;
        this.cdr.markForCheck();
      },
      error: () => console.error('Stundenplan-Namen nicht geladen!'),
    });
  }

  private getCourseType(id: number): CourseType {
    return this.courseTypes.find((c) => c.courseTypeId === id);
  }

  private getLocation(id: number): Location {
    return this.locations.find((l) => l.locationId === id);
  }

  formatDateForInput(date: Date | null): string | null {
    if (!date) return null;
    const yyyy = date.getFullYear();
    const mm = (date.getMonth() + 1).toString().padStart(2, '0');
    const dd = date.getDate().toString().padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  }

  onSaveCourse() {
    const courseToSave = {
      courseId: this.editedCourse.courseId,
      courseName: this.editedCourse.courseName,
      startDate: this.formatDateToString(this.editedCourse.startDateObj),
      endDate: this.formatDateToString(this.editedCourse.endDateObj),
      locationId: this.editedCourse.locationId,
      courseTypeId: this.editedCourse.courseTypeId,
      // null leert Beruf bzw. Stundenplan-Name im Backend
      occupationId: this.editedCourse.occupationId ?? null,
      timetableName: this.editedCourse.timetableName?.trim() || null,
      ihk: this.editedCourse.ihk,
      isActive: this.editedCourse.isActive,
      apt1Date: this.formatDateToString(this.editedCourse.apt1DateObj),
      apt2Date: this.formatDateToString(this.editedCourse.apt2DateObj),
      users: this.editedCourse.users ?? [],
    };

    this.courseService.updateCourse(courseToSave).subscribe({
      next: (response) => {
        this.notificationService.success(
          `Kurs ${courseToSave.courseName} erfolgreich geändert`,
          response,
        );

        // Nach Update Cache neu laden und Subject aktualisieren:
        this.courseService.reloadCourses();
        this.dialogManagerService.closeCourseListCourseDetailDialog();
        this.isEditing = false;
      },
      error: () => {
        this.notificationService.error(
          `Kurs ${courseToSave.courseName} NICHT geändert`,
        );
      },
    });
  }

  //  Datum als String formatieren
  private formatDateToString(date: Date | null): string | null {
    if (!date) return null;
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');

    return `${year}-${month}-${day}`;
  }

  cancelEditing() {
    this.isEditing = false;
  }
  //#endregion

  //#region Teilnehmende
  async showDetails(course: Course): Promise<void> {
    const availableUsers = [...this.allUsers];
    this.dialogManagerService.openCourseUsersDialog(
      course.courseName,
      course,
      availableUsers,
    );
  }
  // #endregion
}
